<template>
  <!-- Floating Chat Button -->
  <button
    @click="handleShowAI"
    :disabled="showAIDisabled"
    class="bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold shadow w-full mb-4"
  >
    amazing HELP button!
  </button>

  <div
    class="flex flex-col bg-white rounded-lg shadow-xl"
    :class="isOpen ? 'h-[415px]' : 'h-[56px]'"
    style="transition: height 0.3s ease"
  >
    <!-- Header (visible only when open) -->
    <div
      v-if="isOpen"
      class="bg-blue-500 text-white rounded-t-lg flex justify-between items-center px-4 py-2"
    >
      <h3 class="text-lg font-semibold">Chat</h3>
      <button
        @click="toggleChat"
        class="text-white hover:text-gray-200"
        aria-label="Close chat"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m19.5 8.25-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>
    </div>

    <!-- Messages Area (visible only when open) -->

    <div
      v-if="isOpen"
      ref="messagesContainer"
      class="flex-1 p-4 overflow-y-auto bg-gray-50"
      style="min-height: 0"
    >
      <div
        v-for="(message, index) in messages"
        :key="index"
        class="mb-3 flex"
        :class="message.type === 'sent' ? 'justify-end' : 'justify-start'"
      >
        <!-- IMAGE ONLY → NO BUBBLE AT ALL -->
        <img
          v-if="message.image && !message.text"
          :src="message.image"
          class="max-w-[230px] max-h-[230px] rounded-xl border-2 border-blue-500 shadow-md"
          alt="Screenshot"
        />

        <!-- TEXT (or TEXT + IMAGE) → BUBBLE -->
        <div
          v-else-if="message.text || message.image"
          :class="[
            'global-chat-message-body p-3 rounded-lg shadow-sm max-w-[260px]',
            message.type === 'sent'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-900',
          ]"
        >
          <img
            v-if="false"
            :src="message.image"
            class="max-w-[200px] max-h-[200px] rounded mb-2 border-2 border-blue-500 shadow-md"
          />
          <p v-if="message.text" v-html="renderMarkdown(message.text)" class="whitespace-pre-wrap break-words"></p>
        </div>
      </div>

      <!-- AI Typing Loader at the bottom -->
      <div
        v-if="isThinking"
        class="ai-thinking self-start justify-center items-center"
      >
        <div class="dots flex justify-center items-center">
          <span></span><span></span><span></span>
        </div>
      </div>
    </div>

    <!-- Input Area: always visible -->
    <div
      class="border-t border-gray-200 p-3 flex items-center gap-2 bg-white rounded-b-lg"
      :class="isOpen ? '' : 'rounded-b-lg'"
    >
      <textarea
        v-model="newMessage"
        @input="autoResize"
        @keydown.enter.exact.prevent="sendMessage"
        placeholder="Ask anything..."
        rows="1"
        ref="textarea"
        class="flex-grow resize-none border border-gray-300 rounded-md p-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
      ></textarea>

      <button
        @click="sendMessage"
        :disabled="showAIDisabled"
        class="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 disabled:opacity-50 disabled:hover:bg-blue-500 transition-colors"
        aria-label="Send message"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
          />
        </svg>
      </button>
    </div>
  </div>

  <!-- Drag Selection Overlay -->
  <div
    v-if="selecting"
    class="fixed inset-0 z-40 cursor-crosshair"
    @mousedown="startSelection"
    @mousemove="updateSelection"
    @mouseup="endSelection"
  >
    <div
      v-if="selection"
      class="absolute border-2 border-blue-400 bg-blue-200/30"
      :style="selectionStyle"
    ></div>
  </div>
</template>

<script>
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import html2canvas from "html2canvas";
import Tesseract from "tesseract.js";
import axios from "axios";

export default {
  name: "GlobalChat",
  data() {
    return {
      isOpen: false,
      newMessage: "",
      messages: [],
      selectedImage: null,
      selectedImageText: null,
      screenshotMode: false,
      selecting: false,
      startX: 0,
      startY: 0,
      endX: 0,
      endY: 0,
      selection: null,
      threadId: null,
      isLoading: false,
      isThinking: false,
      showAIDisabled: false,
    };
  },
  computed: {
    selectionStyle() {
      if (!this.selection) return {};
      const { left, top, width, height } = this.selection;
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      };
    },
  },
  
  mounted() {
    this.restoreThreadId();
    this.trackPageLoad();
  },

  watch: {
    '$route'(to, from) {
      // Track module view when route changes
      if (to.path !== from.path) {
        this.trackModuleView();
      }
    }
  },

  methods: {
    /** Render minimal Markdown for AI replies: bold (**text**) and lists (- or * at line start). Safe for v-html. */
    renderMarkdown(text) {
      if (!text || typeof text !== "string") return "";
      const escape = (s) =>
        String(s)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      let out = escape(text);
      out = out.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      out = out.replace(/\*(.+?)\*/g, "<em>$1</em>");
      const lines = out.split("\n");
      const result = [];
      let inList = false;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const listMatch = /^[\s]*[-*]\s+(.+)$/.exec(line);
        if (listMatch) {
          if (!inList) {
            result.push("<ul>");
            inList = true;
          }
          result.push("<li>" + listMatch[1] + "</li>");
        } else {
          if (inList) {
            result.push("</ul>");
            inList = false;
          }
          result.push(line + (i < lines.length - 1 ? "\n" : ""));
        }
      }
      if (inList) result.push("</ul>");
      return result.join("");
    },

    generateSessionId() {
      return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    generateClientEventId() {
      return 'evt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    restoreThreadId() {
      const saved = JSON.parse(localStorage.getItem("chat_thread"));

      if (saved) {
        const now = Date.now();
        const expiryMs = saved?.ttl;

        if (now - saved?.savedAt < expiryMs) {
          this.threadId = saved?.id;
        } else {
          localStorage.removeItem("chat_thread");
        }
      }
    },

    toggleChat() {
      this.isOpen = !this.isOpen;
    },

    autoResize() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;

      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    },

    resetUI() {
      this.isLoading = false;
      this.isThinking = false;
      this.newMessage = "";
      this.selectedImage = null;
      this.selectedImageText = null;
      this.showAIDisabled = false;
    },

    async trackPageLoad() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return; // Only track if user is logged in
        
        const creator = JSON.parse(localStorage.getItem("creator") || "{}");
        const apiUrl = `${process.env.VUE_APP_API_V2_URL}/api/ai/guide-chat-v2`;
        
        const payload = {
          session_id: this.threadId || this.generateSessionId(),
          event_type: 'page_load',
          user_name: creator?.firstName || creator?.name || "",
          trial_days_left: creator?.trialDaysLeft || null,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          active_module: this.$route.meta.activeModule || window.location.pathname.split("/")[1] || "unknown",
          screen_id: this.$route.meta.activeScreen || "unknown",
          user_id: creator?.id || undefined,
          client_event_id: this.generateClientEventId(),
          metadata: {
            timestamp: Date.now(),
            userAgent: navigator.userAgent,
            page: window.location.pathname
          }
        };
        
        await axios.post(apiUrl, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        
      } catch (error) {
        console.error("Failed to track page load:", error);
      }
    },

    async trackModuleView() {
      try {
        const token = localStorage.getItem("token");
        const creator = JSON.parse(localStorage.getItem("creator") || "{}");
        const apiUrl = `${process.env.VUE_APP_API_V2_URL}/api/ai/guide-chat-v2`;
        
        const activeModule = this.$route.meta.activeModule || window.location.pathname.split("/")[1] || "unknown";
        
        const payload = {
          session_id: this.threadId || this.generateSessionId(),
          event_type: 'module_view',
          user_name: creator?.firstName || creator?.name || "",
          trial_days_left: creator?.trialDaysLeft || null,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          active_module: activeModule,
          screen_id: this.$route.meta.activeScreen || "unknown",
          user_id: creator?.id || undefined,
          client_event_id: this.generateClientEventId(),
          metadata: {
            timestamp: Date.now(),
            userAgent: navigator.userAgent
          }
        };
        
        await axios.post(apiUrl, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        
      } catch (error) {
        console.error("Failed to track module view:", error);
      }
    },

    async endSession() {
      if (!this.threadId) return;
      
      try {
        const token = localStorage.getItem("token");
        const creator = JSON.parse(localStorage.getItem("creator") || "{}");
        const apiUrl = `${process.env.VUE_APP_API_V2_URL}/api/ai/guide-chat-v2`;
        
        const payload = {
          session_id: this.threadId,
          event_type: 'end_session',
          user_name: creator?.firstName || creator?.name || "",
          trial_days_left: creator?.trialDaysLeft || null,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          active_module: this.$route.meta.activeModule || window.location.pathname.split("/")[1] || "unknown",
          screen_id: this.$route.meta.activeScreen || "unknown",
          user_id: creator?.id || undefined,
          client_event_id: this.generateClientEventId(),
          metadata: {
            timestamp: Date.now(),
            userAgent: navigator.userAgent
          }
        };
        
        await axios.post(apiUrl, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        
        // Clear local session
        localStorage.removeItem("chat_thread");
        this.threadId = null;
        this.messages = [];
        
      } catch (error) {
        console.error("Failed to end session:", error);
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() && !this.selectedImage) return;

      this.isLoading = true;
      this.isThinking = true;
      this.isOpen = true;
      this.showAIDisabled = true;

      const message = this.newMessage.trim();

      const canvas = await html2canvas(document.documentElement, {
        useCORS: true,
        allowTaint: true,
        logging: true, // Helpful for debugging persistent issues
        scale: 1,
        backgroundColor: "#ffffff",
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
      });
      const imageData = canvas.toDataURL("image/png");

      this.selectedImage = imageData;
     
      // For auto-captures, show a subtle message
      this.messages.push({
        text: message,
        image: this.selectedImage,
        type: "sent",
      });

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 25000); // 25s timeout

      if (this.selectedImage) {
        const {
          data: { text },
        } = await Tesseract.recognize(this.selectedImage, "eng");

        this.selectedImageText = text;
      }

      try {
        const token = localStorage.getItem("token");
        const creator = JSON.parse(localStorage.getItem("creator") || "{}");
        const apiUrl = `${process.env.VUE_APP_API_V2_URL}/api/ai/guide-chat-v2`;

        // Determine event type and payload structure
        const eventType = (message ? 'help_request_submit' : 'page_load');
        
        // For follow-up messages in existing session
        const isFollowUp = this.threadId && message && !this.selectedImage;
        const finalEventType = isFollowUp ? 'follow_up_submit' : eventType;
        
        const activeModule = this.$route.meta.activeModule ||
          window.location.pathname.split("/")[1] ||
          "unknown";
        
        // V2 Event Payload (preferred)
        const v2Payload = {
          session_id: this.threadId || this.generateSessionId(),
          event_type: finalEventType,
          user_name: creator?.firstName || creator?.name || "",
          trial_days_left: creator?.trialDaysLeft || null,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          active_module: activeModule,
          screen_id: this.$route.meta.activeScreen || "unknown",
          current_screen_description: this.selectedImage ? this.selectedImageText : undefined,
          recent_user_actions: this.selectedImage ? ["clicked amazing HELP button"] : undefined,
          question: message || undefined,
          screenshot: this.selectedImage ? {
            base64: this.selectedImage.replace(/^data:image\/[^;]+;base64,/, ''),
            media_type: "image/png"
          } : undefined,
          user_id: creator?.id || undefined,
          client_event_id: this.generateClientEventId(),
          metadata: {
            timestamp: Date.now(),
            userAgent: navigator.userAgent
          }
        };

        // Legacy Payload (fallback)
        const legacyPayload = {
          session_id: this.threadId || this.generateSessionId(),
          user_id: creator?.id || undefined,
          action: message ? "query_ai" : "help_ai",
          followUpQuery: message || undefined,
          scrnShotUrl: this.selectedImage || undefined,
          context: {
            module: activeModule,
            screen_id: this.$route.meta.activeScreen || "unknown",
            user_name: creator?.firstName || creator?.name || undefined,
            trial_days_left: creator?.trialDaysLeft || undefined,
            screen: this.selectedImage ? this.selectedImageText : undefined,
            actions: this.selectedImage ? ["clicked amazing HELP button"] : undefined
          }
        };

        // Use v2 payload by default, fallback to legacy if v2 fails
        let payload = v2Payload;
        let useLegacy = false;

        this.newMessage = "";
        this.$nextTick(() => {
          const textarea = this.$refs.textarea;
          if (textarea) {
            textarea.style.height = "auto";
          }
        });

        // API Call with fallback logic
        let response;
        try {
          response = await axios.post(apiUrl, payload, {
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
        } catch (v2Error) {
          // If v2 fails, try legacy payload
          console.warn('V2 API failed, trying legacy payload:', v2Error);
          useLegacy = true;
          payload = legacyPayload;
          response = await axios.post(apiUrl, payload, {
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
        }

        clearTimeout(timeout);

        // Handle API response format (v2 or legacy)
        let reply;
        if (!useLegacy && response?.data?.success) {
          // V2 API response
          if (response.data.model_called) {
            // Model was called successfully
            reply = response.data.reply || "I understand your request, but I don't have a specific response.";
            
            // Log additional v2 response data for debugging
            console.log('V2 API Response:', {
              intent: response.data.intent,
              resolved_module: response.data.resolved_module,
              sources: response.data.sources,
              screenshot_received: response.data.screenshot_received
            });
          } else {
            // No model call (non-trigger event)
            reply = response.data.reply || "Event processed successfully.";
            console.log('V2 API - No model call:', response.data.action, response.data.reason);
          }
        } else {
          // Legacy API response or v2 fallback
          reply = response?.data?.reply?.trim() || response?.data?.message || "Sorry, that didn't process. Try again.";
          if (useLegacy) {
            console.log('Legacy API Response:', response.data);
          }
        }

        this.messages.push({
          text: reply,
          type: "received",
        });

        // Save session data (v2 API uses session_id consistently)
        if (response?.data?.session_id) {
          this.threadId = response.data.session_id;
          const existing = JSON.parse(localStorage.getItem("chat_thread"));
          if (!existing) {
            localStorage.setItem(
              "chat_thread",
              JSON.stringify({
                id: response.data.session_id,
                ttl: response?.data?.ttl || 3600000, // 1 hour default
                savedAt: Date.now(),
              })
            );
          }
        }
      } catch (error) {
        console.error("API FAILED", error);

        const isAbort =
          error.name === "CanceledError" ||
          error.code === "ERR_CANCELED" ||
          error.message?.includes("canceled");

        let fallback;
        if (isAbort) {
          fallback = "Request timed out. Try again.";
        } else if (error.response?.status === 400) {
          // Handle v2 API specific error codes
          const errorCode = error.response.data?.error;
          switch (errorCode) {
            case 'INVALID_REQUEST':
              fallback = "Invalid request format. Please try again.";
              break;
            case 'INVALID_EVENT':
              fallback = "This action is not allowed. Please try a different approach.";
              break;
            case 'UNKNOWN_MODULE':
              fallback = "The current module is not recognized. Please navigate to a valid module.";
              break;
            default:
              fallback = "Bad request. Please try again.";
          }
        } else if (error.response?.status === 500) {
          fallback = "Server error. Please try again in a moment.";
        } else {
          fallback = "Sorry, that didn't process. Try again.";
        }

        this.messages.push({
          text: fallback,
          type: "received",
        });
      } finally {
        this.resetUI();
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    async handleShowAI() {
      try {
        this.showAIDisabled = true;

        // Increased delay to ensure any dynamic help components are fully loaded and rendered
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.isOpen = true;

        /* const canvas = await html2canvas(document.documentElement, {
          useCORS: true,
          allowTaint: true,
          logging: true, // Helpful for debugging persistent issues
          scale: 1,
          backgroundColor: "#ffffff",
          windowWidth: document.documentElement.clientWidth,
          windowHeight: document.documentElement.clientHeight,
        });
        const imageData = canvas.toDataURL("image/png");

        this.selectedImage = imageData;

        this.messages.push({
          type: "sent",
        });

        // Call your existing method
        await this.sendMessage();

        this.$nextTick(() => this.scrollToBottom()); */
      } catch (err) {
        console.error("HELP AI failed:", err);
        this.messages.push({
          text: "Couldn't share screen. Please try again.",
          type: "received",
        });
      } finally {
        this.showAIDisabled = false;
      }
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      container.scrollTop = container.scrollHeight;
    },

    startSelection(e) {
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.selection = {
        left: this.startX,
        top: this.startY,
        width: 0,
        height: 0,
      };
    },

    updateSelection(e) {
      if (!this.selection) return;
      this.endX = e.clientX;
      this.endY = e.clientY;
      const left = Math.min(this.startX, this.endX);
      const top = Math.min(this.startY, this.endY);
      const width = Math.abs(this.startX - this.endX);
      const height = Math.abs(this.startY - this.endY);
      this.selection = { left, top, width, height };
    },

    async endSelection() {
      this.selecting = false;
      const container = document.body;
      const canvas = await html2canvas(container, {
        x: this.selection.left,
        y: this.selection.top,
        width: this.selection.width,
        height: this.selection.height,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
      });

      // Convert canvas to data URL
      const imageData = canvas.toDataURL("image/png");
      this.selectedImage = imageData;

      // Reset selection and open chat if not already open
      this.selection = null;
      if (!this.isOpen) {
        this.isOpen = true;
      }

      // Send the screenshot to the API
      await this.sendMessage();

      // Scroll to bottom to show new message
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
  },
};
</script>

<style>
.global-chat-message-body ul {
  margin: 0.25em 0;
  padding-left: 1.25em;
}
.global-chat-message-body li {
  margin: 0.15em 0;
}
.global-chat-message-body strong {
  font-weight: 700;
}
.global-chat-message-body em {
  font-style: italic;
}

.ai-thinking {
  display: flex;
  align-items: center;
  background: #efefef;
  padding: 8px 10px;
  border-radius: 12px;
  width: fit-content;
  margin: 4px 0;
}
.dots span {
  height: 4px;
  width: 4px;
  margin: 0 3px;
  background: #333;
  border-radius: 50%;
  display: inline-block;
  animation: blink 1s infinite ease-in-out;
}
.dots span:nth-child(2) {
  animation-delay: 0.2s;
}
.dots span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes blink {
  0% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.3);
  }
  100% {
    opacity: 0.2;
    transform: scale(1);
  }
}
</style>
